import { FC, Fragment } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import { DifferentiationValueSelector as AccessLevelValueSelector } from 'components'
import {
    ACTION_TYPES,
    checkAllRequiredFieldTypeFilled,
    checkFieldTypeWithValidSourceValues,
    checkIfAtleastOneAddedValue,
    checkIfValidFreeLablValues,
    FIELD_TYPES_SELECTION_RULE,
    findChangedFieldTypesForAppRight,
    findChangedFieldTypesForAppRole,
    getModifiedFieldTypeValue,
    updateJsonMappingWithApiFormat,
    hasAtleastOneNonFixedFieldType,
    isFixedFieldType,
    removePropertiesFromRights,
    useIsSmallScreen,
    checkIfValueIsAlreadyAssigned,
} from 'packages/core'
import { Skeleton } from '@mui/material'
import { AddToCart } from 'components/AddToCart'
import { IResourceType, ResourceTypesNamespace, useRegistry } from 'core'
import cartHelpers from 'containers/Cart/cartHelpers'
import { useHistory } from 'react-router'
import appConfig from 'config'
import { Loader, useNotification } from 'packages/eid-ui'
import { useResourceTypeContext } from 'resourceTypeContext'
import { useApplicationFieldTypes } from 'hooks'
import { ACCESS_LEVEL_TYPE } from 'utils'
import TextWithIcon from 'components/common/TextWithIcon'

const PromptContainer = styled('div')({
    padding: '48px 31px 20px 32px',
    minHeight: '232px',
    display: 'flex',
})

const PaddedDiv = styled(Box)({
    padding: '0 31px',
})

const Header = styled(Box)(({ theme }) => ({
    boxShadow: '0 0.4rem 1.7rem 0 rgba(0, 0, 0, 0.07)',
    padding: '1.7rem 3.2rem 1.2rem',
    background: `repeating-linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white} 3px, rgba(0, 0, 0, 0.02) 3px, rgba(0, 0, 0, 0.02) 4px)`,
    borderTop: 'solid 0.1rem #e8e8e8',
    borderBottom: 'solid 0.1rem #e8e8e8',
    '& h2': {
        fontSize: '1.4rem',
        fontWeight: '600',
    },
}))

const HelperText = styled(Box)(({ theme }) => ({
    display: 'flex',
    '& svg': {
        height: '1.6rem',
        width: '1.4rem',
        color: '#d8d8dd',
    },
}))

const GradientSpacer = styled(Box)({
    background:
        'linear-gradient(180deg, #e9e9eb 0%, #f7f7f9 50%, #f1f1f3 100%)',
    height: '2.4rem',
})

interface ApplicationAccessLevelProps {
    accessLevels: any
    accessLevel: any
    setAccessLevelValue: (val: any) => void
    isCheckingAccess: boolean
    application: any
    targetPerson: string
    fieldTypeSelector?: any
    isFormValidated?: boolean
    setIsFormValidated?: any
}
const ApplicationAccessLevel: FC<ApplicationAccessLevelProps> = ({
    accessLevels,
    accessLevel,
    targetPerson,
    setAccessLevelValue,
    isCheckingAccess,
    application,
    fieldTypeSelector,
    isFormValidated = true,
    setIsFormValidated,
}) => {
    const { t } = useTranslation()
    const registry = useRegistry()
    const history = useHistory()
    const { showWarningMessage } = useNotification()
    const [
        {
            appRightsFieldTypes,
            selectdAccessLevelAppRoleRight: {
                selectedAppRoleOrRightAlreadyAssigned,
            },
        },
        dispatch,
    ]: any = useResourceTypeContext()

    const applicationsType: IResourceType = registry.get(
        ResourceTypesNamespace,
        'Applications',
    )

    // To Fetch the application fieldType from react-query

    const { data, isFetching } = useApplicationFieldTypes(
        null,
        accessLevel?.resourceAssignment?.resourceTypeName ===
            ACCESS_LEVEL_TYPE.AZURE_ROLE
            ? accessLevel?.resourceAssignment?.id
            : null,
        accessLevel?.resourceAssignment?.resourceTypeName ===
            ACCESS_LEVEL_TYPE.AZ_LOCAL_RIGHT
            ? accessLevel?.resourceAssignment?.id
            : null,
        Boolean(
            application?.isPBACApplication &&
                accessLevel?.resourceAssignment?.hasFieldTypes,
        ),
    )

    const isSmallScreen = useIsSmallScreen()

    const getPreValidationMessage = () => {
        let validateMessage = ''
        if (!Boolean(accessLevel)) {
            validateMessage = t('Common_SelectAccessLevel')
        } else if (isFormValidated) {
            if (!checkIfValidJson()) {
                validateMessage = t('Common_WronglyConfiguredJson')
            } else if (!checkIfAllRequiredFieldFilled()) {
                validateMessage = t('Common_RequiredFieldMissingValues')
            } else if (!checkIfAtleastOneFieldValueIsRequired()) {
                validateMessage = t('Common_SelectAtleastOneFieldValue')
            } else if (!checkIfValidFreeLablValues(appRightsFieldTypes)) {
                validateMessage = t('Common_RequiredUniqueLabels')
            }
        } else if (isFetching) {
            validateMessage = t('Common_Loading_FieldTypes')
        }

        return validateMessage
    }

    const bindUpdatedFieldTypeValues = () => {
        if (data) {
            const originalData = JSON.parse(data)
            const parsedObject =
                originalData &&
                Array.isArray(originalData) &&
                originalData?.length
                    ? originalData[0]
                    : originalData
            const parsedMappedWithApiFormat = updateJsonMappingWithApiFormat(
                parsedObject,
                false,
            )

            if (
                accessLevel?.resourceAssignment?.resourceTypeName ===
                ACCESS_LEVEL_TYPE.AZURE_ROLE
            ) {
                const updatedFieldTypes = removePropertiesFromRights(
                    appRightsFieldTypes,
                    ['isGrouped', 'groupedRights'],
                )
                return updateJsonMappingWithApiFormat(
                    findChangedFieldTypesForAppRole(
                        parsedMappedWithApiFormat,
                        updatedFieldTypes,
                        ['SourceValues', 'totalCount'],
                    ),
                    true,
                )
            } else {
                return updateJsonMappingWithApiFormat(
                    findChangedFieldTypesForAppRight(
                        parsedMappedWithApiFormat,
                        appRightsFieldTypes,
                        ['SourceValues', 'totalCount'],
                    ),
                    true,
                )
            }
        }
    }
    const hasAnyNonFixedFieldType = () => {
        if (data) {
            const originalData = JSON.parse(data)
            const parsedObject =
                originalData &&
                Array.isArray(originalData) &&
                originalData?.length
                    ? originalData[0]
                    : originalData
            const parsedMappedWithApiFormat = updateJsonMappingWithApiFormat(
                parsedObject,
                false,
            )
            return hasAtleastOneNonFixedFieldType(
                parsedMappedWithApiFormat,
                accessLevel?.resourceAssignment?.resourceTypeName ===
                    ACCESS_LEVEL_TYPE.AZURE_ROLE,
            )
        }
    }
    const preAddItem = (baseObj: any) => {
        const itemToAdd = cartHelpers.applicationsAccessToRolesToCartItem({
            targetPerson,
            assignmentType: 'Add',
            application,
            assignmentStatus: accessLevel,
            fieldTypeValueJson:
                appRightsFieldTypes &&
                accessLevel?.resourceAssignment?.hasFieldTypes
                    ? JSON.stringify([bindUpdatedFieldTypeValues()])
                    : null,
            ...baseObj,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const preAddSplitted = (baseObj: any, fieldType: any) => {
        const itemToAdd = cartHelpers.applicationsAccessToRolesToCartItem({
            targetPerson,
            assignmentType: 'Add',
            application,
            assignmentStatus: accessLevel,
            fieldTypeValueJson:
                fieldType && accessLevel?.resourceAssignment?.hasFieldTypes
                    ? JSON.stringify([fieldType])
                    : null,
            ...baseObj,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const preAdd = (baseObj: any) => {
        if (!checkIfValidJson()) {
            showWarningMessage(t('Common_WronglyConfiguredJson'))
            if (setIsFormValidated) {
                setIsFormValidated(true)
            }
            return undefined
        }
        if (!checkIfAllRequiredFieldFilled()) {
            showWarningMessage(t('Common_RequiredFieldMissingValues'))
            if (setIsFormValidated) {
                setIsFormValidated(true)
            }
            return undefined
        }
        if (!checkIfValidFreeLablValues(appRightsFieldTypes)) {
            showWarningMessage(t('Common_RequiredUniqueLabels'))
            if (setIsFormValidated) {
                setIsFormValidated(true)
            }
            return undefined
        }
        if (!checkIfAtleastOneFieldValueIsRequired()) {
            showWarningMessage(t('Common_SelectAtleastOneFieldValue'))
            if (setIsFormValidated) {
                setIsFormValidated(true)
            }
            return undefined
        }
        if (
            appRightsFieldTypes?.SplitBusinessRequestApprovalPerFieldTypeValue
        ) {
            const apiDataResponse = JSON.parse(data)
            const dataObject = Array.isArray(apiDataResponse)
                ? apiDataResponse[0]
                : apiDataResponse

            const parsedObject = updateJsonMappingWithApiFormat(
                dataObject,
                false,
            )
            let cartItems: any = []
            const fieldTypes = appRightsFieldTypes?.FieldType || []
            const orginalFieldTypeData = parsedObject?.FieldType || []
            fieldTypes.forEach((item: any) => {
                const fieldType: any = getModifiedFieldTypeValue(
                    orginalFieldTypeData,
                    item,
                )
                if (
                    fieldType?.isModified ||
                    isFixedFieldType(fieldType?.SelectionRule)
                ) {
                    const parentObject = {
                        ...appRightsFieldTypes,
                    }
                    if (fieldType?.AzFieldTypeID) {
                        const valueType =
                            fieldType?.AzFieldTypeSelectionRuleTypeID ===
                            FIELD_TYPES_SELECTION_RULE.MULTISELECT
                                ? 'AssignedValues'
                                : 'AssignedValue'
                        let fieldValue = fieldType[valueType]

                        if (fieldType?.AssignedAllValuesRange) {
                            const itemToAdd: any = preAddSplitted(baseObj, {
                                ...parentObject,
                                FieldType: [
                                    {
                                        ...fieldType,
                                    },
                                ],
                            })
                            cartItems.push(itemToAdd)

                            return cartItems
                        }

                        if (Array.isArray(fieldValue)) {
                            const fieldToAdd = fieldValue.filter(
                                (x: any) =>
                                    x.ActionType !== ACTION_TYPES.NO_CHANGE,
                            )
                            fieldToAdd.forEach((field: any) => {
                                const itemToAdd: any = preAddSplitted(baseObj, {
                                    ...parentObject,
                                    FieldType: [
                                        {
                                            ...fieldType,
                                            [valueType]: [field],
                                        },
                                    ],
                                })
                                cartItems.push(itemToAdd)
                            })
                        } else {
                            if (
                                fieldType?.AzFieldTypeSelectionRuleTypeID !==
                                    FIELD_TYPES_SELECTION_RULE.MULTISELECT &&
                                fieldValue
                            ) {
                                fieldValue = [fieldValue]
                            }
                            const itemToAdd: any = preAddSplitted(baseObj, {
                                ...parentObject,
                                FieldType: [
                                    {
                                        ...fieldType,
                                        [valueType]: fieldValue,
                                    },
                                ],
                            })
                            cartItems.push(itemToAdd)
                        }
                    }
                }
            })

            if (!cartItems?.length) return

            return cartItems
        } else {
            const itemToAdd: any = preAddItem(baseObj)
            if (!itemToAdd) return undefined

            if (itemToAdd.resourceType_Name === 'AzureRole') {
                itemToAdd.resourceType_Name = 'RoleDefinition'
            }
            return itemToAdd
        }
    }

    const checkIfAtleastOneFieldValueIsRequired = () => {
        if (!hasAnyNonFixedFieldType()) {
            return true //Returning true because the main function hasAnyNonFixedFieldType is being used with a NOT operator
        }
        let isAtleastOneSelected = false
        if (
            accessLevel?.resourceAssignment?.enforceFieldTypeSelection &&
            accessLevel?.resourceAssignment?.hasFieldTypes
        ) {
            const data = { ...bindUpdatedFieldTypeValues() }
            try {
                if (data?.Rights?.length) {
                    data?.Rights?.forEach((right: any) => {
                        if (right?.FieldType?.length) {
                            right?.FieldType?.forEach((item: any) => {
                                if (
                                    checkIfAtleastOneAddedValue(item) ||
                                    checkIfValueIsAlreadyAssigned(item)
                                ) {
                                    isAtleastOneSelected = true
                                }
                            })
                        }
                    })
                } else {
                    if (data?.FieldType?.length) {
                        data?.FieldType?.forEach((item: any) => {
                            if (
                                checkIfAtleastOneAddedValue(item) ||
                                checkIfValueIsAlreadyAssigned(item)
                            ) {
                                isAtleastOneSelected = true
                            }
                        })
                    }
                }
            } catch (err) {}

            return isAtleastOneSelected
        } else {
            return true
        }
    }

    const checkIfAllRequiredFieldFilled = () => {
        const data = { ...appRightsFieldTypes }
        let isRequiredFilled = true
        try {
            if (data?.Rights?.length) {
                data?.Rights?.forEach((right: any) => {
                    if (right?.FieldType?.length) {
                        right?.FieldType?.forEach((item: any) => {
                            if (!checkAllRequiredFieldTypeFilled(item)) {
                                isRequiredFilled = false
                            }
                        })
                    }
                })
            } else {
                if (data?.FieldType?.length) {
                    data?.FieldType?.forEach((item: any) => {
                        if (!checkAllRequiredFieldTypeFilled(item)) {
                            isRequiredFilled = false
                        }
                    })
                }
            }
        } catch (err) {}

        return isRequiredFilled
    }

    const checkIfValidJson = () => {
        const data = { ...appRightsFieldTypes }
        let isValidJson = true
        if (!isFormValidated) {
            return isValidJson
        }

        try {
            if (data?.Rights?.length) {
                data?.Rights?.forEach((right: any) => {
                    if (right?.FieldType?.length) {
                        right?.FieldType?.forEach((item: any) => {
                            if (!checkFieldTypeWithValidSourceValues(item)) {
                                isValidJson = false
                            }
                        })
                    }
                })
            } else {
                if (data?.FieldType?.length) {
                    data?.FieldType?.forEach((item: any) => {
                        if (!checkFieldTypeWithValidSourceValues(item)) {
                            isValidJson = false
                        }
                    })
                }
            }
        } catch (err) {}
        return isValidJson
    }

    return (
        <Fragment>
            {!isSmallScreen && (
                <>
                    <GradientSpacer />
                    <Header>
                        <Typography variant="h2">
                            {t('Common_ApplicationRoleOrRight')}
                        </Typography>
                    </Header>
                    <PromptContainer>
                        <Box
                            width="172px"
                            display="flex"
                            flexDirection="column"
                            zIndex={1}
                        >
                            <Box
                                width="100%"
                                display="flex"
                                justifyContent="center"
                            >
                                <Icon
                                    name="PermissionLevel"
                                    height="150px"
                                    width="150px"
                                    color="#D2D2D9"
                                />
                            </Box>
                        </Box>
                        <Box width="334px" paddingLeft="16px">
                            {isCheckingAccess ? (
                                <Loader />
                            ) : (
                                <AccessLevelValueSelector
                                    getTooltipText={(i: any) => {
                                        let resourceTypeName =
                                            i?.resourceAssignment
                                                ?.resourceTypeName
                                        if (resourceTypeName === 'AzureRole') {
                                            resourceTypeName = 'RoleDefinition'
                                        }
                                        return `${t(
                                            `Common_${resourceTypeName}`,
                                        )}/${
                                            i?.resourceAssignment
                                                ?.description ||
                                            i?.resourceAssignment
                                                ?.friendlyName ||
                                            i?.resourceAssignment?.name
                                        }`
                                    }}
                                    loading={!accessLevels}
                                    onChange={(val: any) =>
                                        setAccessLevelValue(val)
                                    }
                                    data={
                                        accessLevels &&
                                        accessLevels.filter(
                                            (o: any) => !o.isAssigned,
                                        )
                                    }
                                    value={accessLevel?.resourceAssignment.id}
                                    getOptionLabel={(o: any) =>
                                        o.resourceAssignment.friendlyName
                                    }
                                    checkSelectedOption={(
                                        o: any,
                                        selectedValueId: any,
                                    ) =>
                                        o.resourceAssignment.id ===
                                        selectedValueId
                                    }
                                    // NOTE: This is a quick fix as per new Ui. as the component is getting used at other places too.
                                    readOnlyOptions={[]}
                                />
                            )}
                        </Box>
                        {selectedAppRoleOrRightAlreadyAssigned && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '44.2rem',
                                    paddingLeft: '2.8rem',
                                    paddingTop: '.5rem',
                                }}
                            >
                                <HelperText>
                                    <Icon name="Info" />
                                    <Typography
                                        sx={{
                                            fontSize: '1.2rem',
                                            paddingLeft: '1rem',
                                            color: '#3b454d',
                                        }}
                                    >
                                        {t(
                                            'HelperTextAlreadyAssignedAccessLevel',
                                        )}
                                    </Typography>
                                </HelperText>
                            </Box>
                        )}
                        <Box sx={{ ml: '3.2rem' }}>
                            {accessLevel?.resourceAssignment?.instructions && (
                                <TextWithIcon
                                    iconName={'InfoNew'}
                                    message={
                                        accessLevel?.resourceAssignment
                                            ?.instructions
                                    }
                                />
                            )}
                        </Box>
                    </PromptContainer>
                    {fieldTypeSelector}
                    <Box>
                        {!application ? (
                            <Box padding={'24px'}>
                                <Skeleton height={40} />
                            </Box>
                        ) : (
                            <AddToCart
                                useAccessLevelPolicy={true}
                                resourceType={applicationsType}
                                resource={application}
                                secondary={accessLevel}
                                preValidate={getPreValidationMessage}
                                preAdd={preAdd}
                                hideStartDate={true}
                                isMultiCartItems={
                                    appRightsFieldTypes?.SplitBusinessRequestApprovalPerFieldTypeValue ||
                                    false
                                }
                                postAdd={() => {
                                    dispatch({
                                        type: 'UPDATE_APP_RIGHTS_FIELD_TYPES',
                                        payload: null,
                                    })
                                    history.push(
                                        `${appConfig.APP_SUBPATH}/applications`,
                                    )
                                }}
                            />
                        )}
                    </Box>
                </>
            )}

            {isSmallScreen && (
                <PaddedDiv>
                    <Box
                        paddingY="24px"
                        display="flex"
                        flexDirection="column"
                        position="relative"
                    >
                        <Box position="absolute" top="16px" right="0">
                            <Icon
                                name="PermissionLevel"
                                height="100px"
                                width="100px"
                                color="#D2D2D9"
                            />
                        </Box>
                        <Box width="172px" paddingY="4px" zIndex={1}>
                            <Typography
                                style={{
                                    fontSize: '14px',
                                    lineHeight: 1.43,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {t('Common_ApplicationRoleOrRight')}
                            </Typography>
                        </Box>
                        <Box paddingY="16px">
                            <AccessLevelValueSelector
                                getTooltipText={(i: any) => {
                                    let resourceTypeName =
                                        i?.resourceAssignment?.resourceTypeName
                                    if (resourceTypeName === 'AzureRole') {
                                        resourceTypeName = 'RoleDefinition'
                                    }
                                    return `${t(
                                        `Common_${resourceTypeName}`,
                                    )}/${
                                        i?.resourceAssignment?.description ||
                                        i?.resourceAssignment?.friendlyName ||
                                        i?.resourceAssignment?.name
                                    }`
                                }}
                                loading={!accessLevels}
                                onChange={(val: any) =>
                                    setAccessLevelValue(val)
                                }
                                data={
                                    accessLevels &&
                                    accessLevels.filter(
                                        (o: any) => !o.isAssigned,
                                    )
                                }
                                value={
                                    accessLevel?.resourceAssignment
                                        .resourceAssignmentId
                                }
                                getOptionLabel={(o: any) =>
                                    o.resourceAssignment.friendlyName
                                }
                                checkSelectedOption={(
                                    o: any,
                                    selectedValueId: any,
                                ) =>
                                    o.resourceAssignment
                                        .resourceAssignmentId ===
                                    selectedValueId
                                }
                                readOnlyOptions={
                                    accessLevels &&
                                    accessLevels.filter(
                                        (o: any) => o.isAssigned,
                                    )
                                }
                            />
                        </Box>
                        <Box width="100%">
                            {!application ? (
                                <Box padding={'24px'}>
                                    <Skeleton height={40} />
                                </Box>
                            ) : (
                                <AddToCart
                                    resourceType={applicationsType}
                                    resource={application}
                                    secondary={accessLevel}
                                    preValidate={getPreValidationMessage}
                                    preAdd={preAdd}
                                    useAccessLevelPolicy={true}
                                    postAdd={() => {
                                        dispatch({
                                            type: 'UPDATE_APP_RIGHTS_FIELD_TYPES',
                                            payload: null,
                                        })
                                        history.push(
                                            `${appConfig.APP_SUBPATH}/applications`,
                                        )
                                    }}
                                    isSmallScreen={isSmallScreen}
                                />
                            )}
                        </Box>
                    </Box>
                </PaddedDiv>
            )}
        </Fragment>
    )
}

export default ApplicationAccessLevel
